import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API } from '../App';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Calendar } from '../components/ui/calendar';
import { ArrowLeft, Plus, MapPin, Clock, DollarSign, ExternalLink } from 'lucide-react';

const fmtDate = (d) => {
  // YYYY-MM-DD in LOCAL timezone (avoid toISOString UTC shift bug)
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

const emptyForm = () => ({
  job_card: '',
  customer_name: '',
  invoice_amount: '',
  salesperson_id: '',
  job_start_time: '09:00',
  location_from_pin: '',
  notes: '',
});

export default function CalendarPage() {
  const navigate = useNavigate();
  const [month, setMonth] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(null);
  const [jobs, setJobs] = useState([]);
  const [salespersons, setSalespersons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(emptyForm());

  const loadJobs = useCallback(async () => {
    setLoading(true);
    try {
      const [jobsRes, spRes] = await Promise.all([
        axios.get(`${API}/jobs`),
        axios.get(`${API}/salespersons`),
      ]);
      setJobs(jobsRes.data || []);
      setSalespersons(spRes.data || []);
    } catch (err) {
      console.warn('Calendar: failed to load data', err);
      toast.error('Failed to load calendar data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadJobs();
  }, [loadJobs]);

  // Group jobs by date
  const jobsByDate = jobs.reduce((acc, j) => {
    const k = j.date;
    if (!acc[k]) acc[k] = [];
    acc[k].push(j);
    return acc;
  }, {});

  const datesWithJobs = Object.keys(jobsByDate).map(d => new Date(d + 'T00:00:00'));

  const handleDayClick = (day) => {
    if (!day) return;
    const key = fmtDate(day);
    setSelectedDate(key);
    setForm(emptyForm());
  };

  const selectedJobs = selectedDate ? (jobsByDate[selectedDate] || []) : [];

  const openNewBooking = () => {
    if (!selectedDate) {
      toast.error('Pick a date first');
      return;
    }
    setForm(emptyForm());
    setDialogOpen(true);
  };

  const saveBooking = async () => {
    if (!form.customer_name.trim()) { toast.error('Customer name is required'); return; }
    if (!form.salesperson_id) { toast.error('Salesperson is required'); return; }
    const amount = parseFloat(form.invoice_amount);
    if (!amount || amount <= 0) { toast.error('Enter a valid amount'); return; }

    setSaving(true);
    try {
      const job_card = form.job_card.trim() || `BK-${Date.now().toString().slice(-8)}`;
      const payload = {
        job_card,
        customer_name: form.customer_name.trim(),
        invoice_amount: amount,
        total_job_amount: 0,
        date: selectedDate,
        moving_date: selectedDate,
        salesperson_id: form.salesperson_id,
        job_done_by: 'Reef Team',
        payment_status: 'Other',
        payment_status_other: form.notes ? `Booking: ${form.notes}` : 'Booking',
        job_start_time: form.job_start_time || '09:00',
        job_end_time: '18:00',
        location_from: '',
        location_from_pin: form.location_from_pin.trim(),
        location_to: '',
        days: [],
        status: 'draft',
      };
      await axios.post(`${API}/jobs`, payload);
      toast.success('Job booking created as draft');
      setDialogOpen(false);
      setForm(emptyForm());
      await loadJobs();
    } catch (err) {
      const msg = err?.response?.data?.detail || err.message || 'Unknown error';
      toast.error('Failed to save booking: ' + (typeof msg === 'string' ? msg : JSON.stringify(msg)));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="calendar-page">
      <nav className="bg-white border-b border-slate-200 sticky top-0 z-10 backdrop-blur-md bg-white/80">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center">
          <Button variant="ghost" size="sm" onClick={() => navigate('/')} data-testid="calendar-back-btn">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Button>
          <h1 className="text-2xl font-bold text-slate-900 ml-4">Bookings Calendar</h1>
        </div>
      </nav>

      <main className="max-w-7xl mx-auto p-6 md:p-8">
        <div className="grid grid-cols-1 lg:grid-cols-[auto_1fr] gap-6">
          <Card className="shadow-sm border-slate-200">
            <CardHeader>
              <CardTitle>Pick a date</CardTitle>
            </CardHeader>
            <CardContent>
              <Calendar
                mode="single"
                month={month}
                onMonthChange={setMonth}
                selected={selectedDate ? new Date(selectedDate + 'T00:00:00') : undefined}
                onSelect={handleDayClick}
                modifiers={{ hasJob: datesWithJobs }}
                modifiersClassNames={{
                  hasJob: 'relative font-semibold text-blue-700 after:content-[""] after:absolute after:bottom-0.5 after:left-1/2 after:-translate-x-1/2 after:w-1 after:h-1 after:rounded-full after:bg-blue-600',
                }}
                className="border border-slate-200 rounded-lg p-3"
                data-testid="calendar-month-view"
              />
              <p className="text-xs text-slate-500 mt-3">
                Dots indicate dates with existing bookings.
              </p>
            </CardContent>
          </Card>

          <Card className="shadow-sm border-slate-200">
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle data-testid="calendar-selected-title">
                  {selectedDate ? `Bookings on ${selectedDate}` : 'Select a date'}
                </CardTitle>
              </div>
              <Button
                onClick={openNewBooking}
                disabled={!selectedDate}
                className="bg-slate-900 hover:bg-slate-800 text-white"
                data-testid="calendar-add-booking-btn"
              >
                <Plus className="w-4 h-4 mr-2" />
                New Booking
              </Button>
            </CardHeader>
            <CardContent>
              {!selectedDate && (
                <p className="text-sm text-slate-500" data-testid="calendar-empty-hint">
                  Click any date on the calendar to view or add a booking.
                </p>
              )}
              {selectedDate && loading && (
                <p className="text-sm text-slate-500">Loading...</p>
              )}
              {selectedDate && !loading && selectedJobs.length === 0 && (
                <p className="text-sm text-slate-500" data-testid="calendar-no-bookings">
                  No bookings on this date. Click <span className="font-medium">New Booking</span> to add one.
                </p>
              )}
              {selectedDate && !loading && selectedJobs.length > 0 && (
                <div className="space-y-3" data-testid="calendar-bookings-list">
                  {selectedJobs.map((j) => (
                    <div
                      key={j.id}
                      className="border border-slate-200 rounded-lg p-3 hover:bg-slate-50 cursor-pointer flex items-start justify-between gap-3"
                      onClick={() => navigate(`/jobs/${j.id}/edit`)}
                      data-testid={`calendar-booking-${j.id}`}
                    >
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-semibold text-slate-900 truncate">{j.customer_name}</span>
                          <span className="text-xs text-slate-500">#{j.job_card}</span>
                          <span className={`text-xs px-2 py-0.5 rounded-full ${j.status === 'draft' ? 'bg-amber-100 text-amber-800' : 'bg-emerald-100 text-emerald-800'}`}>
                            {j.status}
                          </span>
                        </div>
                        <div className="flex items-center gap-3 mt-1 text-xs text-slate-600 flex-wrap">
                          {j.job_start_time && (
                            <span className="flex items-center gap-1"><Clock className="w-3 h-3" />{j.job_start_time}</span>
                          )}
                          <span className="flex items-center gap-1"><DollarSign className="w-3 h-3" />AED {Number(j.invoice_amount || 0).toFixed(2)}</span>
                          {j.location_from_pin && (
                            <a
                              href={j.location_from_pin}
                              target="_blank"
                              rel="noopener noreferrer"
                              onClick={(e) => e.stopPropagation()}
                              className="flex items-center gap-1 text-blue-600 hover:underline"
                            >
                              <MapPin className="w-3 h-3" />
                              Open pin
                              <ExternalLink className="w-3 h-3" />
                            </a>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </main>

      {/* New Booking Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg" data-testid="calendar-booking-dialog">
          <DialogHeader>
            <DialogTitle>New Booking — {selectedDate}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 mt-2">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label>Job Card # <span className="text-xs text-slate-400">(auto if blank)</span></Label>
                <Input
                  value={form.job_card}
                  onChange={(e) => setForm({ ...form, job_card: e.target.value })}
                  placeholder="auto-generated"
                  data-testid="booking-job-card-input"
                />
              </div>
              <div className="space-y-1">
                <Label>Customer Name <span className="text-red-500">*</span></Label>
                <Input
                  value={form.customer_name}
                  onChange={(e) => setForm({ ...form, customer_name: e.target.value })}
                  required
                  data-testid="booking-customer-input"
                />
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label>Salesperson <span className="text-red-500">*</span></Label>
                <Select
                  value={form.salesperson_id}
                  onValueChange={(v) => setForm({ ...form, salesperson_id: v })}
                >
                  <SelectTrigger data-testid="booking-salesperson-select">
                    <SelectValue placeholder="Select salesperson" />
                  </SelectTrigger>
                  <SelectContent>
                    {salespersons.map((sp) => (
                      <SelectItem key={sp.id} value={sp.id}>{sp.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label>Time</Label>
                <Input
                  type="time"
                  value={form.job_start_time}
                  onChange={(e) => setForm({ ...form, job_start_time: e.target.value })}
                  data-testid="booking-time-input"
                />
              </div>
            </div>
            <div className="space-y-1">
              <Label>Location Pin (Google Maps URL)</Label>
              <Input
                value={form.location_from_pin}
                onChange={(e) => setForm({ ...form, location_from_pin: e.target.value })}
                placeholder="https://maps.google.com/?q=..."
                data-testid="booking-location-pin-input"
              />
            </div>
            <div className="space-y-1">
              <Label>Job Amount (AED) <span className="text-red-500">*</span></Label>
              <Input
                type="number"
                step="0.01"
                value={form.invoice_amount}
                onChange={(e) => setForm({ ...form, invoice_amount: e.target.value })}
                placeholder="0.00"
                data-testid="booking-amount-input"
              />
            </div>
            <div className="space-y-1">
              <Label>Notes</Label>
              <Textarea
                rows={2}
                value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
                placeholder="Optional notes"
                data-testid="booking-notes-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setDialogOpen(false)}
              data-testid="booking-cancel-btn"
            >
              Cancel
            </Button>
            <Button
              onClick={saveBooking}
              disabled={saving}
              className="bg-slate-900 hover:bg-slate-800"
              data-testid="booking-save-btn"
            >
              {saving ? 'Saving...' : 'Save as Draft'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
